const KEYBOARD_NAVIGATION_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowRight",
  "ArrowDown",
  "ArrowLeft",
]);

const KEYBOARD_NAVIGATION_CLASS = "kbd-nav";
const KEYBOARD_SCROLL_CONTAINER = ".wb-tool-dock";

interface ScrollableFocusTarget extends EventTarget {
  closest(selectors: string): Element | null;
  scrollIntoView(options?: ScrollIntoViewOptions): void;
}

function isScrollableFocusTarget(
  target: EventTarget | null,
): target is ScrollableFocusTarget {
  if (target === null) return false;
  const candidate = target as Partial<ScrollableFocusTarget>;
  return (
    typeof candidate.closest === "function" &&
    typeof candidate.scrollIntoView === "function"
  );
}

/**
 * Shows focus only after keyboard/D-pad navigation while keeping pointer
 * interactions free of persistent focus rings on the Chrome 79 floor.
 */
export function installKeyboardNavigationMode(target: Document): () => void {
  let pendingScrollFrame: number | undefined;
  const enable = (event: KeyboardEvent): void => {
    if (KEYBOARD_NAVIGATION_KEYS.has(event.key)) {
      target.body.classList.add(KEYBOARD_NAVIGATION_CLASS);
    }
  };
  const disable = (): void => {
    target.body.classList.remove(KEYBOARD_NAVIGATION_CLASS);
  };
  const keepKeyboardFocusVisible = (event: FocusEvent): void => {
    if (
      !target.body.classList.contains(KEYBOARD_NAVIGATION_CLASS) ||
      !isScrollableFocusTarget(event.target) ||
      event.target.closest(KEYBOARD_SCROLL_CONTAINER) === null
    ) {
      return;
    }
    const focusTarget = event.target;
    const scroll = (): void => {
      if (
        target.body.classList.contains(KEYBOARD_NAVIGATION_CLASS) &&
        target.activeElement === focusTarget
      ) {
        focusTarget.scrollIntoView({ block: "nearest", inline: "center" });
      }
    };
    const view = target.defaultView;
    if (view === null) {
      scroll();
      return;
    }
    if (pendingScrollFrame !== undefined)
      view.cancelAnimationFrame(pendingScrollFrame);
    pendingScrollFrame = view.requestAnimationFrame(() => {
      pendingScrollFrame = undefined;
      scroll();
    });
  };

  target.addEventListener("keydown", enable, true);
  target.addEventListener("pointerdown", disable, true);
  target.addEventListener("mousedown", disable, true);
  target.addEventListener("focusin", keepKeyboardFocusVisible, true);

  return () => {
    const view = target.defaultView;
    if (view !== null && pendingScrollFrame !== undefined)
      view.cancelAnimationFrame(pendingScrollFrame);
    target.removeEventListener("keydown", enable, true);
    target.removeEventListener("pointerdown", disable, true);
    target.removeEventListener("mousedown", disable, true);
    target.removeEventListener("focusin", keepKeyboardFocusVisible, true);
    disable();
  };
}
